import { ErrorHandlingProvider } from '@/providers/error-handling/error-handling.provider';
import { SharedUiColorNameV2 } from '@/shared/ui/theming';
import { UiFormErrorComponent } from '@/ui/form-field/form-error/ui-form-error';
import { UiFormFieldComponent } from '@/ui/form-field/form-field/ui-form-field';
import { UiSelectComponent } from '@/ui/select/ui-select';
import { observeControlValidity$, observeControlValue$ } from '@/utils/angular/forms/reactive-forms/reactive-forms-rxjs-interop.utils';
import { ReferenceListPipe } from '@/utils/reference-lists/pipes/reference-list.pipe';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, type OnInit, inject, output, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, type UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { Observable } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';
import {
    type AdvizeoModalButton,
    type AdvizeoModalComponent,
    injectAdvizeoModalInput
} from '../../../../components/advizeo-modal-spawner/advizeo-modal-spawner.component';
import { AdvizeoInputDirective } from '../../../../directives/advizeo-input/advizeo-input.directive';
import type { UnitCode } from '../../../../interfaces/Unit.interface';
import type { AlertScenarioId } from '../../alerts.interface';
import { MeasureTypeCode } from '../threshold-alert-scenario.interface';
import { ThresholdScenarioService } from '../threshold-scenarios-service/threshold-scenario.service';

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./threshold-scenario-creation-modal.scss'],
    selector: 'threshold-threshold-scenario-creation-modal',
    templateUrl: 'threshold-scenario-creation-modal.html',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        UiFormFieldComponent,
        AdvizeoInputDirective,
        UiFormErrorComponent,
        UiSelectComponent,
        AsyncPipe,
        TranslatePipe,
        ReferenceListPipe
    ]
})
export class ThresholdScenarioCreationModalComponent implements AdvizeoModalComponent<null, AlertScenarioId | null>, OnInit {
    private _translateService = inject(TranslateService);
    private _fb = inject(UntypedFormBuilder);
    private _thresholdAlertScenarioService = inject(ThresholdScenarioService);
    private _errorHandlingProvider = inject(ErrorHandlingProvider);

    title = signal('');
    isDismissable = signal(true);
    buttons = signal<AdvizeoModalButton[]>([]);

    input = injectAdvizeoModalInput<null>();
    close = output<AlertScenarioId | void>();

    form!: UntypedFormGroup;

    measureTypes: MeasureTypeCode[] = Object.values(MeasureTypeCode);
    units$!: Observable<UnitCode[]>;

    ngOnInit() {
        this._translateService
            .stream('PAGES.ALERT_SCENARIO.CREATE_THRESHOLD_SCENARIO')
            .pipe(untilDestroyed(this))
            .subscribe((title: string) => {
                this.title.set(title);
            });

        this.form = this._fb.group({
            name: ['', Validators.required],
            measureType: [null, Validators.required],
            unit: [null, Validators.required]
        });

        this.units$ = observeControlValue$(this.form.controls.measureType).pipe(
            filter(isNotNullish),
            switchMap((measureType: MeasureTypeCode) => this._thresholdAlertScenarioService.getUnitCodesFromMeasureType$(measureType)),
            tap(units => {
                if (!units.includes(this.form.value.unit)) {
                    this.form.controls.unit.setValue(null);
                }
            })
        );

        observeControlValidity$(this.form)
            .pipe(untilDestroyed(this))
            .subscribe(isValid => {
                this.buttons.set([
                    {
                        text: this._translateService.instant('BUTTONS.VALIDATE'),
                        onClick: () => {
                            this.createScenario();
                        },
                        isDisabled: !isValid,
                        color: SharedUiColorNameV2.PRIMARY
                    }
                ]);
            });
    }

    async createScenario() {
        const formValue = this.form.value;
        try {
            const alertScenarioId = await this._thresholdAlertScenarioService.createThresholdAlertScenario({
                name: formValue.name,
                measureType: formValue.measureType,
                unit: formValue.unit
            });
            this.close.emit(alertScenarioId);
        } catch (err) {
            const errors = this._thresholdAlertScenarioService.extractErrorsFromApiFailure(
                this._errorHandlingProvider.getErrorCodeFromApiResponse(err),
                this._errorHandlingProvider.getErrorPayloadFromApiResponse(err)
            );
            this._thresholdAlertScenarioService.showErrors(errors);
        }
    }
}
